import { Eye, EyeOff, Loader2 } from "lucide-react";
import { Trans, useLingui } from "@lingui/react/macro";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

type EmailPasswordFormProps = {
  email: string;
  setEmail: (email: string) => void;
  password: string;
  setPassword: (password: string) => void;
  showPassword: boolean;
  setShowPassword: (show: boolean) => void;
  authMethod: "password" | "magic-link";
  setAuthMethod: (method: "password" | "magic-link") => void;
  isLoading: boolean;
  hasEmailError: boolean;
  hasPasswordError: boolean;
  canSubmit: boolean;
  handleSubmit: (e: React.FormEvent) => void;

  honeypotWebsite: string;
  setHoneypotWebsite: (value: string) => void;
  honeypotCompany: string;
  setHoneypotCompany: (value: string) => void;
};

export function EmailPasswordForm({
  email,
  setEmail,
  password,
  setPassword,
  showPassword,
  setShowPassword,
  authMethod,
  setAuthMethod,
  isLoading,
  hasEmailError,
  hasPasswordError,
  canSubmit,
  handleSubmit,

  honeypotWebsite,
  setHoneypotWebsite,
  honeypotCompany,
  setHoneypotCompany,
}: EmailPasswordFormProps) {
  const { t } = useLingui();
  return (
    <form className="space-y-4" onSubmit={handleSubmit}>
      {/* Honeypot fields - hidden from humans, filled by bots */}
      <input
        aria-hidden="true"
        autoComplete="off"
        className="-left-[9999px] absolute h-0 w-0"
        name="website"
        onChange={(e) => setHoneypotWebsite(e.target.value)}
        tabIndex={-1}
        type="text"
        value={honeypotWebsite}
      />
      <input
        aria-hidden="true"
        autoComplete="off"
        className="-left-[9999px] absolute h-0 w-0"
        name="company_name"
        onChange={(e) => setHoneypotCompany(e.target.value)}
        tabIndex={-1}
        type="text"
        value={honeypotCompany}
      />

      <div>
        <Label htmlFor="email" className="sr-only">{t`Email address`}</Label>
        <div className="relative">
          <Input
            aria-describedby={hasEmailError ? "email-error" : undefined}
            aria-invalid={hasEmailError}
            autoComplete="email"
            className="border-gray-200 bg-gray-50 text-gray-900 placeholder:text-gray-400 focus:border-gold-accent focus:ring-gold-accent"
            id="email"
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t`Email address`}
            type="email"
            value={email}
          />
        </div>
        {hasEmailError && (
          <p className="mt-2 text-red-500 text-xs" id="email-error" role="alert">
            <Trans>Please enter a valid email address</Trans>
          </p>
        )}
      </div>

      {authMethod === "password" && (
        <div>
          <Label htmlFor="password" className="sr-only">{t`Password`}</Label>
          <div className="relative">
            <Input
              aria-describedby={hasPasswordError ? "password-error" : undefined}
              aria-invalid={hasPasswordError}
              autoComplete="new-password"
              className="border-gray-200 bg-gray-50 pr-10 text-gray-900 placeholder:text-gray-400 focus:border-gold-accent focus:ring-gold-accent"
              id="password"
              onChange={(e) => setPassword(e.target.value)}
              placeholder={t`Password (min 8 characters)`}
              type={showPassword ? "text" : "password"}
              value={password}
            />
            <button
              className="-translate-y-1/2 absolute top-1/2 right-3 text-gray-400 hover:text-gray-600"
              onClick={() => setShowPassword(!showPassword)}
              type="button"
            >
              {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
          {hasPasswordError && (
            <p className="mt-2 text-red-500 text-xs" id="password-error" role="alert">
              <Trans>Password must be at least 8 characters and cannot start or end with spaces</Trans>
            </p>
          )}
        </div>
      )}

      <div className="pt-2">
        <Button
          className="w-full bg-gold-accent font-semibold text-base text-gray-900 transition-all hover:bg-gold-accent/90 focus:ring-2 focus:ring-gold-accent focus:ring-offset-2 focus:ring-offset-white disabled:cursor-not-allowed disabled:opacity-50 sm:text-lg"
          disabled={!canSubmit}
          size="lg"
          type="submit"
        >
          {isLoading ? (
            <>
              <Loader2 className="mr-2 h-5 w-5 animate-spin" />
              {authMethod === "password" ? t`Creating account...` : t`Sending magic link...`}
            </>
          ) : authMethod === "password" ? (
            <Trans>Create Account</Trans>
          ) : (
            <Trans>Send Magic Link</Trans>
          )}
        </Button>
      </div>

      <p className="text-center text-gray-500 text-xs">
        {authMethod === "password" ? (
          <button
            className="font-medium text-gold-accent transition-opacity hover:opacity-80"
            onClick={() => setAuthMethod("magic-link")}
            type="button"
          >
            <Trans>Sign up with magic link instead</Trans>
          </button>
        ) : (
          <button
            className="font-medium text-gold-accent transition-opacity hover:opacity-80"
            onClick={() => setAuthMethod("password")}
            type="button"
          >
            <Trans>Sign up with password instead</Trans>
          </button>
        )}
      </p>
    </form>
  );
}
